// PKCE (RFC 7636) helpers for the web sign-in. The verifier stays in the browser tab that starts the sign-in;
// only its S256 challenge goes out with the request for the sign-in URL.

const VERIFIER_BYTES = 32;
const UNAVAILABLE_MESSAGE = 'PKCE is not available in this browser context';

const toBase64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// Returns a 43-character base64url verifier. Throws when Web Crypto is not available.
export const generateCodeVerifier = (): string => {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error(UNAVAILABLE_MESSAGE);
  }
  return toBase64Url(globalThis.crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));
};

// Returns the base64url SHA-256 of the verifier. Rejects when crypto.subtle is not available, which is the case
// outside a secure context (HTTPS or localhost).
export const deriveCodeChallenge = async (verifier: string): Promise<string> => {
  if (!globalThis.crypto?.subtle) {
    throw new Error(UNAVAILABLE_MESSAGE);
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
};
